import { z } from 'zod';

import { createOffsetPaginationRequestQuerySchema } from './pagination';
import { ProjectSchema } from './projects';
import { SourceSchema } from './sources';
import { PrimaryKeySchema, commaDelimitedList } from './utils';

/**
 * A project source is a link between a project and a file source
 *
 * This entity will exist if a user has explicitly added a source to a project.
 */
export const ProjectSourceSchema = z.object({
  projectSourceId: PrimaryKeySchema,
  projectId: ProjectSchema.shape.projectId,
  sourceId: SourceSchema.shape.sourceId,
  createdAt: z.coerce.date(),
});

export type ProjectSource = z.infer<typeof ProjectSourceSchema>;

export const ProjectSourcesSchema = z.array(ProjectSourceSchema);

export type ProjectSources = z.infer<typeof ProjectSourcesSchema>;

export const ProjectSourcesQuerySchema = z
  .object({
    projectSourceIds: commaDelimitedList(
      ProjectSourceSchema.shape.projectSourceId.array(),
    ).optional(),
    projectIds: commaDelimitedList(
      ProjectSchema.shape.projectId.array(),
    ).optional(),
    sourceIds: commaDelimitedList(
      SourceSchema.shape.sourceId.array(),
    ).optional(),
  })
  .merge(createOffsetPaginationRequestQuerySchema(z.enum(['createdAt'])));

export type ProjectSourcesQuery = z.infer<typeof ProjectSourcesQuerySchema>;

export const DeleteProjectSourcesQuerySchema = ProjectSourcesQuerySchema.pick({
  projectIds: true,
  sourceIds: true,
}).required();

export type DeleteProjectSourcesQuery = z.infer<
  typeof DeleteProjectSourcesQuerySchema
>;

export const CreateProjectSourceRequestSchema = z.object({
  projectId: ProjectSchema.shape.projectId,
  sourceId: SourceSchema.shape.sourceId,
});

export type CreateProjectSourceRequest = z.infer<
  typeof CreateProjectSourceRequestSchema
>;

export const CreateProjectSourcesRequestSchema = z.array(
  CreateProjectSourceRequestSchema,
);

export type CreateProjectSourcesRequest = z.infer<
  typeof CreateProjectSourcesRequestSchema
>;
